import { useState, useEffect, useMemo, useCallback, useDeferredValue } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BookOpen, FileText, BarChart2, Gauge, ChevronDown, ChevronLeft, List, PlayCircle, Scale, Search, X } from 'lucide-react';
import userGuideRaw from '../content/help/userGuide.md?raw';
import nec22087Raw from '../content/help/nec22087.md?raw';
import amiDataRaw from '../content/help/amiData.md?raw';
import directMeasurementRaw from '../content/help/directMeasurement.md?raw';
import choosingMethodRaw from '../content/help/choosingMethod.md?raw';
import { GuideOutline } from './GuideOutline';
import {
  splitGuide,
  buildOutline,
  outlineLabel,
  searchTerms,
  sectionMatches,
  rehypeHighlight,
  type GuideSection,
} from '../lib/helpSearch';

type SectionId = 'user-guide' | 'choosing-a-method' | 'nec-220-87' | 'ami-data' | 'direct-measurement';

const SECTIONS = [
  {
    id: 'user-guide' as SectionId,
    title: 'User Guide',
    icon: BookOpen,
    description: 'Step-by-step instructions for completing a load evaluation with TrueLoad.',
    content: userGuideRaw,
  },
  {
    id: 'choosing-a-method' as SectionId,
    title: 'Choosing a Method',
    icon: Scale,
    description: 'Measured demand vs. the traditional calculation — when to use each, and how the report basis works.',
    content: choosingMethodRaw,
  },
  {
    id: 'nec-220-87' as SectionId,
    title: 'Measured Demand Method',
    icon: FileText,
    description: 'The code methodology behind TrueLoad’s measured-demand calculation (NEC 220.87; Section 120.87 under the 2026 edition).',
    content: nec22087Raw,
  },
  {
    id: 'ami-data' as SectionId,
    title: 'AMI Data',
    icon: BarChart2,
    description: 'What interval data is, how to request it, and what to do if your utility isn’t supported.',
    content: amiDataRaw,
  },
  {
    id: 'direct-measurement' as SectionId,
    title: 'Direct Measurement',
    icon: Gauge,
    description: 'How to establish existing demand using a revenue-grade meter when AMI data isn’t available.',
    content: directMeasurementRaw,
  },
];

const ACCENT = '#185FA5';

const proseClasses = `
  [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:mb-6
  [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:border-b-2 [&_h2]:border-gray-200 [&_h2]:pb-2 [&_h2]:mt-8 [&_h2]:mb-4
  [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-gray-800 [&_h3]:mt-5 [&_h3]:mb-2
  [&_p]:text-gray-700 [&_p]:leading-relaxed [&_p]:mb-3
  [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3 [&_ul]:space-y-1
  [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3 [&_ol]:space-y-1
  [&_li]:text-gray-700 [&_li]:leading-relaxed
  [&_strong]:font-semibold [&_strong]:text-gray-900
  [&_em]:italic
  [&_hr]:border-gray-200 [&_hr]:my-6
  [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:text-gray-600 [&_blockquote]:italic [&_blockquote]:my-3
  [&_table]:w-full [&_table]:border-collapse [&_table]:mb-4 [&_table]:text-sm
  [&_thead]:bg-gray-50
  [&_th]:border [&_th]:border-gray-300 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-gray-700
  [&_td]:border [&_td]:border-gray-300 [&_td]:px-3 [&_td]:py-2 [&_td]:text-gray-700 [&_td]:align-top
  [&_code]:bg-gray-100 [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-sm [&_code]:font-mono
  [&_pre]:bg-gray-100 [&_pre]:rounded [&_pre]:p-3 [&_pre]:mb-3 [&_pre]:overflow-x-auto
`;

/** A search result is one section of the guide in its own card, so its
 *  heading sits at the top of the card rather than carrying the document's
 *  between-section margin. */
const resultClasses = 'rounded-lg border border-gray-200 p-4 mb-4 [&_h2]:!mt-0 [&_h3]:!mt-0 [&>:last-child]:mb-0';

const anchorId = (index: number) => `help-section-${index}`;

/** Height of the app's sticky header-and-tabs block, so jumps land below it
 *  and the outline can sit under it. */
function useStickyHeaderHeight(): number {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const el = document.querySelector<HTMLElement>('[data-sticky-header]');
    if (!el) return;
    const measure = () => setHeight(el.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return height;
}

interface HelpTabProps {
  /** Plays the guided demo on a sample job, parking whatever is open. */
  onStartDemo?: () => void;
}

export function HelpTab({ onStartDemo }: HelpTabProps) {
  const [activeSection, setActiveSection] = useState<SectionId | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get('section');
    return SECTIONS.find((sec) => sec.id === s) ? (s as SectionId) : null;
  });
  const [query, setQuery] = useState('');
  // The box stays responsive while a long result list renders behind it.
  const deferredQuery = useDeferredValue(query);
  // A jump from a result or the outline: which section to scroll to once the full guide is shown.
  const [scrollTarget, setScrollTarget] = useState<number | null>(null);
  // The outline: the section under the reading line, which `##` entries are open, the phone panel.
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const [outlineOpen, setOutlineOpen] = useState(false);
  const headerHeight = useStickyHeaderHeight();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get('section');
    if (s && SECTIONS.find((sec) => sec.id === s)) {
      setActiveSection(s as SectionId);
    }
  }, []);

  const navigate = useCallback((sectionId: SectionId | null) => {
    setActiveSection(sectionId);
    setQuery('');
    setOutlineOpen(false);
    setActiveIndex(null);
    if (sectionId) {
      window.history.pushState({}, '', `?section=${sectionId}`);
    } else {
      window.history.pushState({}, '', window.location.pathname);
    }
  }, []);

  const currentSection = activeSection ? SECTIONS.find((s) => s.id === activeSection) : null;
  const isGuide = currentSection?.id === 'user-guide';

  const sections = useMemo(
    () => (currentSection ? splitGuide(currentSection.content) : []),
    [currentSection],
  );
  const outline = useMemo(() => (isGuide ? buildOutline(sections) : []), [isGuide, sections]);
  const terms = useMemo(() => (isGuide ? searchTerms(deferredQuery) : []), [isGuide, deferredQuery]);
  const results = useMemo(
    () => (terms.length > 0 ? sections.filter((s) => s.level > 0 && sectionMatches(s, terms)) : null),
    [sections, terms],
  );

  const components = useMemo<Components>(
    () => ({
      a({ href, children }) {
        if (href?.startsWith('?section=')) {
          const sectionId = href.replace('?section=', '') as SectionId;
          return (
            <button
              type="button"
              onClick={() => navigate(sectionId)}
              className="text-blue-600 underline hover:text-blue-800"
            >
              {children}
            </button>
          );
        }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">
            {children}
          </a>
        );
      },
      mark({ children }) {
        return <mark className="bg-yellow-200 text-inherit rounded-sm px-0.5">{children}</mark>;
      },
    }),
    [navigate],
  );

  // The whole document, one block per section so a result can jump to its place.
  const fullDocument = useMemo(
    () =>
      sections.map((s) => (
        <div key={s.index} id={anchorId(s.index)}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
            {s.markdown}
          </ReactMarkdown>
        </div>
      )),
    [sections, components],
  );

  const active = activeIndex !== null ? sections[activeIndex] : undefined;
  const activeParentIndex = active ? (active.level === 2 ? active.index : (active.parentIndex ?? active.index)) : null;
  const activeParentLabel = activeParentIndex !== null ? outlineLabel(sections[activeParentIndex].heading).title : null;

  // Which section is under the reading line: the last one whose top has passed
  // it, or the last section of all once the page is scrolled to its end.
  const syncActive = useCallback(() => {
    if (!isGuide || results !== null) return;
    const line = headerHeight + 32;
    const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
    let current: number | null = null;
    for (const s of sections) {
      if (s.level === 0) continue;
      const el = document.getElementById(anchorId(s.index));
      if (!el) continue;
      if (atBottom || el.getBoundingClientRect().top <= line) current = s.index;
      else break;
    }
    setActiveIndex(current);
  }, [isGuide, results, sections, headerHeight]);

  // Kept current as the page scrolls. Scroll events already arrive at most
  // once a frame, so the handler reads the positions directly.
  useEffect(() => {
    if (!isGuide || results !== null) return;
    syncActive();
    window.addEventListener('scroll', syncActive, { passive: true });
    window.addEventListener('resize', syncActive);
    return () => {
      window.removeEventListener('scroll', syncActive);
      window.removeEventListener('resize', syncActive);
    };
  }, [isGuide, results, syncActive]);

  // Once the full guide is rendered, land the chosen section just below the
  // sticky header and tab bar, and mark it as the one being read at once.
  useEffect(() => {
    if (scrollTarget === null || results !== null) return;
    const el = document.getElementById(anchorId(scrollTarget));
    if (el) {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - (headerHeight + 16) });
    }
    setScrollTarget(null);
    syncActive();
  }, [scrollTarget, results, headerHeight, syncActive]);

  // The outline follows the reader: moving into a section opens it and closes
  // the rest. Anything opened by hand stays open until the next move.
  useEffect(() => {
    setExpanded(new Set(activeParentIndex === null ? [] : [activeParentIndex]));
  }, [activeParentIndex]);

  const toggleExpanded = useCallback((index: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  function showInGuide(section: GuideSection) {
    setQuery('');
    setOutlineOpen(false);
    setScrollTarget(section.index);
  }

  if (currentSection) {
    const shownQuery = deferredQuery.trim();
    const outlineProps = {
      outline,
      activeIndex,
      activeParentIndex,
      expanded,
      onToggle: toggleExpanded,
      onSelect: showInGuide,
    };
    return (
      <div
        className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 mx-auto ${isGuide ? 'max-w-6xl' : 'max-w-4xl'}`}
      >
        <button
          type="button"
          onClick={() => navigate(null)}
          className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 mb-6"
        >
          <ChevronLeft size={16} />
          Back to Help
        </button>
        {isGuide && (
          /* Phone and tablet: a bar that stays under the app header, names the
             section being read, and opens the outline. */
          <div
            className="lg:hidden sticky z-30 -mx-6 px-6 mb-4 bg-white border-b border-gray-200"
            style={{ top: headerHeight }}
          >
            <button
              type="button"
              onClick={() => setOutlineOpen((open) => !open)}
              aria-expanded={outlineOpen}
              data-testid="help-guide-contents-toggle"
              className="w-full flex items-center gap-2 py-3 text-sm"
            >
              <List size={16} className="flex-shrink-0 text-gray-500" />
              <span className="font-semibold text-gray-900">Contents</span>
              {activeParentLabel && <span className="truncate text-gray-500">· {activeParentLabel}</span>}
              <ChevronDown
                size={16}
                className={`ml-auto flex-shrink-0 text-gray-400 transition-transform ${outlineOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {outlineOpen && <GuideOutline {...outlineProps} className="max-h-[60vh] pb-3" />}
          </div>
        )}
        <div className={isGuide ? 'lg:flex lg:gap-8' : undefined}>
        {isGuide && (
          <aside className="hidden lg:block w-60 flex-shrink-0">
            <GuideOutline
              {...outlineProps}
              className="sticky"
              style={{ top: headerHeight + 24, maxHeight: `calc(100vh - ${headerHeight + 48}px)` }}
            />
          </aside>
        )}
        <div className="min-w-0 flex-1">
        {isGuide && (
          <div className="mb-6">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                enterKeyHint="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setQuery('');
                }}
                placeholder="Search the guide"
                aria-label="Search the user guide"
                data-testid="help-guide-search"
                className="w-full rounded-lg border border-gray-300 pl-9 pr-9 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            {results && (
              <p className="mt-2 text-sm text-gray-500" data-testid="help-guide-search-summary" aria-live="polite">
                {results.length === 0
                  ? `Nothing in the guide matches “${shownQuery}”.`
                  : `${results.length} ${results.length === 1 ? 'section matches' : 'sections match'} “${shownQuery}”.`}
              </p>
            )}
          </div>
        )}
        <div className={proseClasses}>
          {results
            ? results.map((s) => (
                <section key={s.index} className={resultClasses} data-testid="help-guide-result">
                  <div className="flex items-baseline justify-between gap-3 mb-2 text-xs">
                    <span className="uppercase tracking-wide text-gray-400">{s.parent}</span>
                    <button
                      type="button"
                      onClick={() => showInGuide(s)}
                      className="font-medium text-blue-600 hover:text-blue-800 whitespace-nowrap"
                    >
                      Show in guide
                    </button>
                  </div>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[[rehypeHighlight, terms]]}
                    components={components}
                  >
                    {s.markdown}
                  </ReactMarkdown>
                </section>
              ))
            : fullDocument}
        </div>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Help</h2>
      <p className="text-gray-500 text-sm mb-6">Select a topic to learn more.</p>
      {onStartDemo && (
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <PlayCircle size={20} className="text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Watch a complete evaluation</h3>
              <p className="text-sm text-gray-600 leading-snug">
                A guided demo that fills in a sample job end to end — empty form to
                finished report — explaining what each field feeds. Partway through you pick
                the method it runs, measured demand or the traditional calculation, and can
                see both. Runs on sample data; your own project is parked and handed back
                afterwards.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onStartDemo}
            data-testid="btn-help-start-demo"
            className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0"
          >
            Play the demo
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SECTIONS.map((section) => {
          const Icon = section.icon;
          return (
            <button
              key={section.id}
              type="button"
              onClick={() => navigate(section.id)}
              className="text-left rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                style={{ backgroundColor: `${ACCENT}18` }}
              >
                <Icon size={20} style={{ color: ACCENT }} />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">{section.title}</h3>
              <p className="text-sm text-gray-500 leading-snug">{section.description}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
